import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { partiesApi } from "@/api/parties";
import { portalAccountsApi } from "@/api/portalAccounts";
import type { MaterialAudience } from "@/api/portalMaterials";
import type {
  CreatePartyRequest,
  OwnedEntityRequest,
  PartyContactRequest,
  PartyListQuery,
  PartyType,
  UpdatePartyRequest,
} from "@/types/parties";
import { partiesKeys } from "./partiesKeys";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";

// Party read-flip (WS-A) — reads + mutations for the Borrower/Seller party
// surfaces. Every mutation invalidates the parties prefix; create also
// invalidates the portal-absorption prefix (the create auto-provisions a
// standing portal account that PortalAccountCard renders).

function invalidateParties(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: partiesKeys.all });
}

export function usePartyList(type: PartyType, query: PartyListQuery) {
  return useQuery({
    queryKey: partiesKeys.list(type, query),
    queryFn: () => partiesApi.list(type, query),
  });
}

export function usePartyDetail(type: PartyType, id: string | undefined) {
  return useQuery({
    queryKey: partiesKeys.detail(type, id),
    queryFn: () => partiesApi.get(type, id!),
    enabled: !!id,
  });
}

export function useCreateParty(type: PartyType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePartyRequest) => partiesApi.create(type, body),
    onSuccess: () => {
      invalidateParties(queryClient);
      // The create minted (or matched) a standing portal account.
      void queryClient.invalidateQueries({ queryKey: portalAbsorptionKeys.all });
    },
  });
}

export function useUpdateParty(type: PartyType, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePartyRequest) => partiesApi.update(type, id, body),
    onSuccess: () => invalidateParties(queryClient),
  });
}

export function useArchiveParty(type: PartyType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partiesApi.archive(type, id),
    onSuccess: () => invalidateParties(queryClient),
  });
}

export function useUpsertPartyContact(type: PartyType, partyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PartyContactRequest) =>
      partiesApi.upsertContact(type, partyId, body),
    onSuccess: () => invalidateParties(queryClient),
  });
}

export function useDeletePartyContact(type: PartyType, partyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) =>
      partiesApi.deleteContact(type, partyId, contactId),
    onSuccess: () => invalidateParties(queryClient),
  });
}

export function useUpsertOwnedEntity(type: PartyType, partyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OwnedEntityRequest) =>
      partiesApi.upsertOwnedEntity(type, partyId, body),
    onSuccess: () => invalidateParties(queryClient),
  });
}

export function useDeleteOwnedEntity(type: PartyType, partyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ownedEntityId: string) =>
      partiesApi.deleteOwnedEntity(type, partyId, ownedEntityId),
    onSuccess: () => invalidateParties(queryClient),
  });
}

/** WS-C — explicit portal invite for a party account. Zero bundles: the
 *  backend's signup-link resend arm sends a set-password invitation for an
 *  unactivated account (or a sign-in notification once activated). Invite is
 *  deliberately separate from the mint-only auto-provision at create. */
export function useSendPartyPortalInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { email: string; fullName: string; accountType: MaterialAudience }) =>
      portalAccountsApi.sendSignupLink({ ...vars, bundleIds: [] }),
    onSuccess: () => {
      invalidateParties(queryClient);
      void queryClient.invalidateQueries({ queryKey: portalAbsorptionKeys.all });
    },
  });
}
